import { Box, Checkbox, FormControlLabel, FormLabel, Grid, ListItem, ListItemButton, ListItemIcon, ListItemText, Switch } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { CompanySelect, StandardDatePicker, StandardSelect } from '../../../Components/CommonInputs';
import { FormInput, LoadingIndicator } from '../../../Components/CoreLib/library';
import { FormSection } from '../../../Components/Forms';
import { BusinessComponentSoftwareType, BusinessComponentType, RespondentDto, WorkItemDescriptionDisplayMode, WorkItemType } from '../../../dtos';
import { useGetCompanyRespondentsQuery } from '../../../store';
import { BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES, BUSINESS_COMPONENT_TYPE_DISPLAY_VALUES, WORK_ITEM_DISPLAY_MODE_DISPLAY_VALUES, WORK_ITEM_TYPE_DISPLAY_VALUES, WorkItemFormManger } from '../Hooks/useWorkItemForm';

export interface IAddEditWorkItemFormProps {
    formManager: WorkItemFormManger;
    companyId: string;
    hideAssociatedRespondents?: boolean;
}

export const AddEditWorkItemForm: FC<IAddEditWorkItemFormProps> = ({ formManager, companyId, hideAssociatedRespondents }) => {
    const { data: companyRespondents, isLoading: isLoadingCompanyRespondents } = useGetCompanyRespondentsQuery({
        parentId: companyId,
        page: 0,
        pageSize: 5000,
    });

    const handleToggleRespondent = useCallback(
        (respondentId: string) => {
            const currentItems = formManager.associatedEmployeeIds;
            var isCurrentlySelected = currentItems.includes(respondentId);
            if (isCurrentlySelected) {
                formManager.handleAssociatedEmployeesIdChange(currentItems.filter((x) => x !== respondentId));
            } else {
                formManager.handleAssociatedEmployeesIdChange([...currentItems, respondentId]);
            }
        },
        [formManager]
    );

    const isEveryItemChecked = useMemo(
        () => formManager.associatedEmployeeIds.length === companyRespondents?.totalQueryResults,
        [formManager.associatedEmployeeIds, companyRespondents]
    );
    const isAtLeastOneItemChecked = useMemo(() => formManager.associatedEmployeeIds.length > 0, [formManager.associatedEmployeeIds]);
    const handleToggleSelectAll = useCallback(() => {
        if (!companyRespondents) {
            return;
        }
        if (isEveryItemChecked) {
            formManager.handleAssociatedEmployeesIdChange([]);
        } else {
            formManager.handleAssociatedEmployeesIdChange(companyRespondents.pageResults.map((respondent) => respondent.id));
        }
    }, [isEveryItemChecked, companyRespondents, formManager]);

    const renderEmployeeRow = useCallback(
        (item: RespondentDto): JSX.Element => {
            return (
                <ListItem key={item.id} component='div' disablePadding dense>
                    <ListItemButton onClick={() => handleToggleRespondent(item.id)} dense>
                        <ListItemIcon>
                            <Checkbox
                                edge='start'
                                checked={formManager.associatedEmployeeIds.includes(item.id)}
                                tabIndex={-1}
                                disableRipple
                                size='small'
                                sx={{ height: 16 }}
                            />
                        </ListItemIcon>
                        <ListItemText primary={`${item.firstName} ${item.lastName}`} />
                    </ListItemButton>
                </ListItem>
            );
        },
        [handleToggleRespondent, formManager.associatedEmployeeIds]
    );

    return (
        <>
            {formManager.isTypeSelectVisible && (
                <Grid item xs={12}>
                    <StandardSelect
                        label='Type'
                        isRequired
                        errorMessage={formManager.fieldErrors.get('type')}
                        selectedValue={(formManager.type !== null && WORK_ITEM_TYPE_DISPLAY_VALUES.get(formManager.type)) || ''}
                        handleSelectedValueChange={(value) => formManager.handleTypeChange(value)}
                        options={[
                            '',
                            WORK_ITEM_TYPE_DISPLAY_VALUES.get(WorkItemType.Activity)!,
                            WORK_ITEM_TYPE_DISPLAY_VALUES.get(WorkItemType.BusinessComponent)!,
                        ]}
                    />
                </Grid>
            )}
            <Grid item xs={12}>
                <FormInput
                    value={formManager.name}
                    onChange={formManager.handleNameChange}
                    label='Name'
                    name='name'
                    errorText={formManager.fieldErrors.get('name')}
                    error={!!formManager.fieldErrors.get('name')}
                    fullWidth
                    required
                />
            </Grid>
            {formManager.type === WorkItemType.BusinessComponent && (
                <Grid item xs={12} container direction='row' alignItems='center' wrap='nowrap' gap={1}>
                    <StandardSelect
                        label='Business Component Type'
                        selectedValue={(formManager.bcType !== null && BUSINESS_COMPONENT_TYPE_DISPLAY_VALUES.get(formManager.bcType)) || ''}
                        handleSelectedValueChange={(value) => formManager.handleBcTypeChange(value)}
                        options={[
                            '',
                            BUSINESS_COMPONENT_TYPE_DISPLAY_VALUES.get(BusinessComponentType.Product)!,
                            BUSINESS_COMPONENT_TYPE_DISPLAY_VALUES.get(BusinessComponentType.Process)!,
                            BUSINESS_COMPONENT_TYPE_DISPLAY_VALUES.get(BusinessComponentType.Other)!,
                        ]}
                        isRequired
                        errorMessage={formManager.fieldErrors.get('bcType')}
                    />
                    <StandardSelect
                        label='Software Type'
                        selectedValue={(formManager.bcSoftwareType !== null && BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES.get(formManager.bcSoftwareType)) || ''}
                        handleSelectedValueChange={(value) => formManager.handleBcSoftwareTypeChange(value)}
                        options={[
                            '',
                            BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES.get(BusinessComponentSoftwareType.NA)!,
                            BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES.get(BusinessComponentSoftwareType.IUS)!,
                            BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES.get(BusinessComponentSoftwareType.DFS)!,
                            BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES.get(BusinessComponentSoftwareType.NONISU)!,
                            BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES.get(BusinessComponentSoftwareType.EXCEPTED)!,
                        ]}
                        isRequired
                        errorMessage={formManager.fieldErrors.get('bcSoftwareType')}
                    />
                </Grid>
            )}
            <Grid item xs={12} container direction='row' alignItems='center' wrap='nowrap' gap={1}>
                <FormInput
                    value={formManager.description}
                    onChange={formManager.handleDescriptionChange}
                    label='Description'
                    name='description'
                    errorText={formManager.fieldErrors.get('description')}
                    error={!!formManager.fieldErrors.get('description')}
                    fullWidth
                />
                <FormControlLabel
                    control={<Switch checked={formManager.isDescriptionBulleted} onChange={(_, val) => formManager.handleIsDescriptionBulletedChange(val)} />}
                    label='Split On Comma?'
                    labelPlacement='start'
                    sx={{ width: 250, marginTop: 2 }}
                />
            </Grid>
            <Grid item xs={12}>
                <StandardSelect
                    label='When to Display Description'
                    isRequired
                    errorMessage={formManager.fieldErrors.get('descriptionDisplayMode')}
                    selectedValue={
                        (formManager.descriptionDisplayMode !== null && WORK_ITEM_DISPLAY_MODE_DISPLAY_VALUES.get(formManager.descriptionDisplayMode)) || ''
                    }
                    handleSelectedValueChange={(value) => formManager.handleDescriptionDisplayModeChange(value)}
                    options={[
                        WORK_ITEM_DISPLAY_MODE_DISPLAY_VALUES.get(WorkItemDescriptionDisplayMode.Show)!,
                        WORK_ITEM_DISPLAY_MODE_DISPLAY_VALUES.get(WorkItemDescriptionDisplayMode.Hide)!,
                        WORK_ITEM_DISPLAY_MODE_DISPLAY_VALUES.get(WorkItemDescriptionDisplayMode.Hover)!,
                    ]}
                />
            </Grid>
            {formManager.isCompanySelectVisible && (
                <Grid item xs={12}>
                    <CompanySelect
                        selectedCompanyId={formManager.companyId}
                        handleSelectedCompanyChange={formManager.handleCompanyChange}
                        errorMessage={formManager.fieldErrors.get('companyId')}
                        isRequired
                    />
                </Grid>
            )}
            <Grid item container direction='row' justifyContent='space-between' alignItems='center'>
                <Grid item xs={4}>
                    <StandardDatePicker
                        label='Start Date'
                        value={formManager.startDate}
                        handleDateChange={formManager.handleStartDateChange}
                        errorMessage={formManager.fieldErrors.get('startDate')}
                        required
                    />
                </Grid>
                <Grid item pt={3}>
                    <FormControlLabel
                        control={<Switch checked={formManager.isOngoing} onChange={(_, val) => formManager.handleIsOngoingChange(val)} />}
                        label='Ongoing'
                        labelPlacement='start'
                    />
                </Grid>
                <Grid item xs={4}>
                    <StandardDatePicker
                        label='End Date'
                        value={formManager.endDate}
                        handleDateChange={formManager.handleEndDateChange}
                        errorMessage={formManager.fieldErrors.get('endDate')}
                        required={!formManager.isOngoing}
                        disabled={formManager.isOngoing}
                    />
                </Grid>
                {formManager.type === WorkItemType.BusinessComponent && (
                    <>
                        <Grid item xs={12}>
                            <FormInput
                                value={formManager.bcInformationSought ?? ''}
                                onChange={formManager.handleBcInformationSoughtChange}
                                label='Information Sought'
                                fullWidth
                                errorText={formManager.fieldErrors.get('bcInformationSought')}
                                error={!!formManager.fieldErrors.get('bcInformationSought')}
                                rows={3}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormInput
                                value={formManager.bcProcessOfExperimentation ?? ''}
                                onChange={formManager.handleBcProcessOfExperimentationChange}
                                label='Process Of Experimentation'
                                fullWidth
                                errorText={formManager.fieldErrors.get('bcProcessOfExperimentation')}
                                error={!!formManager.fieldErrors.get('bcProcessOfExperimentation')}
                                rows={3}
                            />
                        </Grid>
                    </>
                )}
                {!hideAssociatedRespondents && (
                    <Grid item xs={12}>
                        <Box height={16} />
                        <FormLabel>Associated Respondents</FormLabel>
                        <FormSection sx={{ maxHeight: 240, overflowY: 'auto' }}>
                            {isLoadingCompanyRespondents && <LoadingIndicator />}
                            {!isLoadingCompanyRespondents && companyRespondents && (
                                <ListItem key='associated-employee-select-all' component='div' disablePadding dense>
                                    <ListItemButton onClick={() => handleToggleSelectAll()} dense>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge='start'
                                                checked={isEveryItemChecked}
                                                indeterminate={!isEveryItemChecked && isAtLeastOneItemChecked}
                                                tabIndex={-1}
                                                disableRipple
                                                size='small'
                                                sx={{ height: 16 }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText primaryTypographyProps={{ fontWeight: 'bold' }} primary='Select All' />
                                    </ListItemButton>
                                </ListItem>
                            )}
                            {!isLoadingCompanyRespondents && companyRespondents?.pageResults.map(renderEmployeeRow)}
                        </FormSection>
                    </Grid>
                )}
            </Grid>
        </>
    );
};
